import React from 'react'
import { FormField, FormMultiSelectField, FormSelectField } from '@/components/Form/FormField'
import { Button } from '@/components/ui/button'
import { DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { NameDto } from 'api/Common/Types'
import { InstrumentDto } from 'api/Trading-Accounts/Types/InstrumentDto'
import {
  Instrument,
  StandardCommission,
} from 'api/Trading-Accounts/Types/IntroducingBrokerDetailedProfileDto'
import { IntroducingBrokerProfileAccountType } from 'api/Trading-Accounts/Types/IntroducingBrokerProfileAccountType'
import { Form, FormikHelpers, FormikProvider, useFormik } from 'formik'
import * as Yup from 'yup'

interface StandardCommissionFormValues {
  id?: string
  tradingAccountTypes: number[]
  amount: number
  platformTypes: number[]
  rebateCalculationMethodId: number
  instruments: string[]
}

interface OuterProps {
  introducingBrokerProfileAccountType: IntroducingBrokerProfileAccountType[]

  data?: StandardCommission
  instrumentTypes: NameDto[]
  platformTypes: NameDto[]
  rebateCalcMethods: NameDto[]

  handleChange(values: Partial<StandardCommission>): void
  instruments: (Instrument | InstrumentDto)[]
}

const validationSchema = Yup.object().shape({
  rebateCalculationMethodId: Yup.number()
    .min(1, 'Rebate Calc. Method is required')
    .required('Rebate Calc. Method is required'),
  amount: Yup.number().when('rebateCalculationMethodId', {
    is: (v: number) => v === 3,
    then: () =>
      Yup.number()
        .min(0.0, 'Rebate Amount must be at least 0.0') // then apply this validation
        .max(100, 'Rebate Amount cannot exceed 100'),
    otherwise: () =>
      Yup.number()
        .min(0.0, 'Rebate Amount must be at least 0.0') // then apply this validation
        .max(100, 'Rebate Amount cannot exceed 100')
        .required('Rebate Amount is required'),
  }),
})

const POINT_BASED_REBATE_CALC_METHOD_ID = 5

export const InstrumentCommissionModal: React.FC<OuterProps> = ({
  introducingBrokerProfileAccountType,
  handleChange,
  data,
  platformTypes,
  rebateCalcMethods,
  instruments,
}) => {
  const initialValues: StandardCommissionFormValues = {
    tradingAccountTypes: data?.tradingAccountTypes?.map((ta) => ta.id) || [],
    amount: data?.amount || 0,
    platformTypes: data?.platformTypes?.map((pt) => pt.id) || [],
    rebateCalculationMethodId: data?.rebateCalculationMethod?.id || 0,
    instruments: data?.instruments?.map((instrument) => instrument.id) || [],
  }

  const filteredRebateCalcMethods = rebateCalcMethods.filter(
    ({ id }) => id !== POINT_BASED_REBATE_CALC_METHOD_ID
  )

  const onSubmit = async (
    values: StandardCommissionFormValues,
    { setSubmitting }: FormikHelpers<StandardCommissionFormValues>
  ) => {
    handleChange(dataToSubmit(values))
    setSubmitting(false)
  }
  const formik = useFormik<StandardCommissionFormValues>({
    initialValues,
    onSubmit,
    validationSchema,
  })

  const mapAndFilter = <T, U>(array: T[] | undefined, mapFn: (item: T) => U | undefined): U[] => {
    return array?.map(mapFn).filter((item): item is U => item !== undefined) ?? []
  }

  const dataToSubmit = (values: StandardCommissionFormValues): StandardCommission => {
    const tradingAccountTypes = mapAndFilter(values.tradingAccountTypes, (type) =>
      introducingBrokerProfileAccountType.find((tat) => tat.id === type)
    )

    const selectedPlatformTypes = mapAndFilter(values.platformTypes, (type) =>
      platformTypes.find((pt) => pt.id === type)
    ).map((pt) => ({
      id: pt?.id,
      name: pt.name,
    }))

    const rebateCalculationMethod = filteredRebateCalcMethods.find(
      (g) => g.id === Number(values.rebateCalculationMethodId)
    )
    const selectedInstruments = mapAndFilter(values.instruments, (type) =>
      instruments.find((instrument) => instrument.id === type)
    )

    return {
      tradingAccountTypes,
      amount: values.amount,
      platformTypes: selectedPlatformTypes,
      rebateCalculationMethod: rebateCalculationMethod!,
      instruments: selectedInstruments,
    }
  }

  const allOption: NameDto<number> = { id: -1, name: 'All' }

  const handleSelectChange = (value: number[] | string[] | undefined, name: string) => {
    const { id: allOptionId } = allOption

    if (Array.isArray(value) && value?.some((item) => item === allOptionId)) {
      const selectionLength = value.length
      if (value[selectionLength - 1] === allOptionId) {
        formik.setFieldValue(name, [allOptionId])
      } else {
        formik.setFieldValue(name, [value[selectionLength - 1]])
      }
    } else {
      formik.setFieldValue(name, value ?? [])
    }
  }

  return (
    <FormikProvider value={formik}>
      <DialogContent size='sm' render={<Form />}>
        <DialogHeader>
          <DialogTitle>
            {data ? (
              <>
                Edit instrument commission for platform:{' '}
                {data?.platformTypes?.map((pt) => pt.name).join(',')}
              </>
            ) : (
              'Add New Instrument Commission'
            )}
          </DialogTitle>
        </DialogHeader>
        <FormMultiSelectField
          name='platformTypes'
          label='Platform'
          value={formik.values.platformTypes}
          options={[allOption, ...platformTypes]}
          showSearch
          allowClear
          dataTest='servers-field'
          onChange={(value) => handleSelectChange(value, 'platformTypes')}
        />
        <FormMultiSelectField
          name='instruments'
          label='Instrument'
          value={formik.values.instruments}
          options={[
            { id: allOption.id, name: allOption.name },
            ...instruments.map((instrument) => ({
              id: instrument.id,
              name: instrument.symbol,
            })),
          ]}
          dataTest='platformTypeId-field'
          onChange={(value) => handleSelectChange(value, 'instruments')}
        />
        <FormMultiSelectField
          name='tradingAccountTypes'
          label='Account Type'
          value={formik.values.tradingAccountTypes}
          options={[allOption, ...introducingBrokerProfileAccountType]}
          dataTest='tradingAccountTypes-field'
          onChange={(value) => handleSelectChange(value, 'tradingAccountTypes')}
        />
        <FormSelectField
          name='rebateCalculationMethodId'
          label='Rebate Calc. Method'
          value={formik.values.rebateCalculationMethodId || undefined}
          options={filteredRebateCalcMethods}
          error={formik.errors.rebateCalculationMethodId}
          onChange={(value) => {
            formik.setFieldValue('rebateCalculationMethodId', Number(value))
            formik.validateField('rebateCalculationMethodId')
          }}
        />
        <FormField
          name='amount'
          label='Rebate Amount'
          type='number'
          min={Number(formik.values.rebateCalculationMethodId) === 3 ? 0.01 : 1}
          max={100}
          value={formik.values.amount}
          error={formik.errors.amount}
          onChange={(event) => formik.setFieldValue('amount', Number(event.target.value))}
        />
        <DialogFooter>
          <Button
            type='button'
            disabled={!formik.isValid || formik.isSubmitting}
            onClick={() => formik.handleSubmit()}
          >
            {data ? 'Edit' : 'Add'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </FormikProvider>
  )
}
